import React from 'react';
import { FragmentParagraphCalloutParagraphFragment } from '@/utils/drupal-api/__generated__/graphql';
import { ImageSrcSetSize } from '@/utils/image';
import clsx from 'clsx';

import { CmsButton } from '@/components/ui/cms-button';

import MediaUnionComponent from '../media_union';

import './callout_paragraph.scss';

import paragraphStyles from './paragraph.module.scss';

type CalloutParagraphProps = {
  data: FragmentParagraphCalloutParagraphFragment;
  className?: string;
  imageSizes?: ImageSrcSetSize[];
  imageAspect?: number;
  unionType?: string;
  id?: string;
};

interface ClassConfig {
  [key: string]: string;
}

// interface AspectConfig {
//   [key: string]: number;
// }

function aspectRatioClass(input: string): string {
  return input.replace(/_/g, '/');
}

export const CalloutParagraph: React.FC<CalloutParagraphProps> = ({
  data,
  className,
  imageSizes,
  unionType,
  id,
}) => {
  const isInverted = data.inverted || false;
  const isRollover = data.rollover || false;
  const mediaPosition = (data.mediaPosition as string) || 'default';
  const isGrid = unionType === 'ParagraphCalloutGrid';
  const contentSplit = data.contentSplit || null;
  const mediaAspect = data.mediaAspect || null;
  const alignmentClasses: string[] = [];
  const buttonAlignmentClasses: string[] = [];
  switch (data.alignmentHorizontal) {
    case 'inherit':
      alignmentClasses.push('justify-[inherit]');
      alignmentClasses.push('text-inherit');
      break;
    case 'left':
      buttonAlignmentClasses.push('self-start');
      alignmentClasses.push('justify-start');
      alignmentClasses.push('text-left');
      break;
    case 'center':
      buttonAlignmentClasses.push('self-center');
      alignmentClasses.push('justify-center');
      alignmentClasses.push('text-center');
      break;
    case 'right':
      buttonAlignmentClasses.push('self-end');
      alignmentClasses.push('justify-end');
      alignmentClasses.push('text-right');
      break;
  }
  switch (data.alignmentVertical) {
    case 'inherit':
      alignmentClasses.push('items-inherit');
      break;
    case 'top':
      alignmentClasses.push('items-start');
      break;
    case 'middle':
      alignmentClasses.push('items-center');
      break;
    case 'bottom':
      alignmentClasses.push('items-end');
      break;
  }
  const getWrapperClass = (mediaPosition: string, isGrid: boolean): string => {
    const baseClass = 'relative ';
    const classes: ClassConfig = {
      left: clsx(
        baseClass,
        'sm:flex',
        'sm:gap-8',
        'lg:gap-12',
        'max-w-screen-2xl',
        alignmentClasses
      ),
      right: clsx(
        baseClass,
        'sm:flex',
        'sm:gap-8',
        'lg:gap-12',
        'max-w-screen-2xl',
        alignmentClasses
      ),
      background:
        isGrid && isRollover
          ? 'aspect-video lg:aspect-square relative w-full h-full'
          : isGrid
            ? 'relative w-full h-full lg:aspect-square'
            : 'relative w-full py-12',
      inline: clsx(baseClass, mediaAspect && 'object-cover', 'max-w-screen-lg'),
      icon: 'relative max-w-screen-lg',
      default: clsx(baseClass, 'max-w-screen-lg', 'mx-auto'),
    };

    return classes[mediaPosition] || classes.default;
  };

  const getMediaClass = (mediaPosition: string): string => {
    const classes: ClassConfig = {
      left:
        'mt-4 sm:my-4 order-1 sm:pl-4 ' +
        (contentSplit == '60_media_40_text'
          ? ' sm:w-1/2 md:w-3/5'
          : contentSplit == '60_text_40_media'
            ? ' sm:w-1/2 md:w-2/5'
            : ' sm:w-1/3 lg:w-1/2'),
      right:
        'mt-4 sm:my-4 order-2 sm:pr-4 ' +
        (contentSplit == '60_media_40_text'
          ? ' sm:w-1/2 md:w-3/5'
          : contentSplit == '60_text_40_media'
            ? ' sm:w-1/2 md:w-2/5'
            : ' sm:w-1/3 lg:w-1/2'),
      background: 'absolute inset-0 -z-10 overflow-hidden',
      inline:
        'w-full flex justify-center items-center flex-col mx-auto ' +
        (isGrid ? ' mb-0 rounded-xl overflow-hidden' : ' mb-4'),
      default: '',
    };

    return classes[mediaPosition] || classes.default;
  };

  const getImageClass = (mediaPosition: string): string => {
    const classes: ClassConfig = {
      left: 'sm:rounded-2xl w-full h-full object-cover',
      right: 'sm:rounded-2xl w-full h-full object-cover',
      background: 'object-cover w-full h-full',
      inline:
        '' +
        (mediaAspect &&
          `aspect-[${aspectRatioClass(mediaAspect)}] object-cover`),
      icon: 'w-10 h-10 mr-4',
      default: '',
    };

    return classes[mediaPosition] || classes.default;
  };

  const getContentClass = (mediaPosition: string): string => {
    // const contentClass = '';
    const classes: ClassConfig = {
      left:
        'order-2 ' +
        (contentSplit == '60_media_40_text'
          ? ' sm:w-1/2 md:w-2/5'
          : contentSplit == '60_text_40_media'
            ? ' sm:w-1/2 md:w-3/5'
            : ' sm:w-2/3 lg:w-1/2'),
      right:
        'order-1' +
        (contentSplit == '60_media_40_text'
          ? ' sm:w-1/2 md:w-2/5'
          : contentSplit == '60_text_40_media'
            ? ' sm:w-1/2 md:w-3/5'
            : ' sm:w-2/3 lg:w-1/2'),
      background: isRollover
        ? `absolute inset-0 mx-auto p-4 flex flex-col h-full w-full group ${alignmentClasses.join(' ')}`
        : `relative m-auto max-w-screen-lg md:px-8 py-12 md:py-24 flex flex-col h-full ${alignmentClasses.join(' ')}`,
      icon: isInverted ? 'relative' : '',
      default: '',
    };

    return classes[mediaPosition] || classes.default;
  };

  const getMediaAspect = (
    mediaPosition: string,
    mediaAspect?: string
  ): number | undefined => {
    if (mediaAspect) {
      const [width, height] = mediaAspect.split('_');
      return parseInt(width) / parseInt(height);
    }
    return undefined;
    // const aspectMap: AspectConfig = {
    //   left: 1,
    //   right: 1,
    //   background: 3,
    //   inline: 1.618,
    //   default: 1.5,
    // };

    // return aspectMap[mediaPosition] || aspectMap.default;
  };

  const CalloutHeader = () => {
    return (
      <>
        {(data.header || data.subHeader) && (
          <div
            className={clsx(
              mediaPosition === 'icon' && 'flex items-center',
              isRollover && 'space-y-1.5',
              'w-full'
            )}
          >
            {mediaPosition === 'icon' && data.mediaItem && (
              <MediaUnionComponent
                data={data.mediaItem}
                figureClassName="inline-block"
                imageSizes={[{ image: 40, viewport: 768 }]}
                imageClassName={getImageClass(mediaPosition)}
                noLoader={true}
              />
            )}
            <div
              className={clsx(
                mediaPosition === 'icon' && 'flex flex-col',
                ['left', 'right'].includes(mediaPosition) && 'space-y-2'
              )}
            >
              {data.header && (
                <h2
                  className={clsx(
                    isInverted ? 'text-white drop-shadow-md' : 'text-black-1',
                    ['left', 'right'].includes(mediaPosition) && 'h3',
                    mediaPosition === 'icon' && 'h3',
                    isRollover && 'h3 md:h4',
                    isGrid && 'h3 md:h4'
                  )}
                >
                  {data.header}
                </h2>
              )}
              {data.subHeader && (
                <h3
                  className={clsx(
                    isInverted ? 'text-white drop-shadow-md' : 'text-black-1',
                    ['left', 'right'].includes(mediaPosition) && 'h6',
                    mediaPosition === 'icon' && 'h5',
                    mediaPosition === 'background' && 'h4',
                    isRollover && 'h5',
                    isGrid && 'h5'
                  )}
                >
                  {data.subHeader}
                </h3>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  const CalloutContent = () => {
    return (
      <div
        style={{
          backgroundColor:
            data.bgColor && isRollover ? `#${data.bgColor}` : undefined,
        }}
        className={clsx(
          isRollover &&
            'absolute h-full w-full space-y-3 overflow-hidden opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100'
        )}
      >
        <div
          className={clsx(
            'flex h-full flex-col space-y-4',
            alignmentClasses,
            isRollover &&
              'translate-y-8 transition-transform duration-300 ease-in-out will-change-transform group-hover:translate-y-0'
          )}
        >
          {CalloutHeader()}
          {data.html?.processed && (
            <div
              className={clsx(
                'wysiwyg space-y-4',
                isInverted ? 'text-white drop-shadow-md' : 'text-grey-8',
                isRollover && 'text-center',
                alignmentClasses
              )}
              dangerouslySetInnerHTML={{ __html: data.html.processed }}
            />
          )}
          {data.links && (
            <div
              className={clsx(
                'flex flex-wrap gap-4',
                alignmentClasses,
                buttonAlignmentClasses,
                mediaPosition === 'background' && 'col-span-2'
              )}
            >
              {data.links.map((link, index) => (
                <CmsButton link={link} key={index} isInverted={isInverted} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const getImageSizes = () => {
    if (imageSizes) {
      return imageSizes;
    }
    switch (mediaPosition) {
      case 'left':
      case 'right':
        return [
          { image: 380, viewport: 375 },
          { image: 420, viewport: 420 },
          { image: 600, viewport: 600 },
          { image: 320, viewport: 800 },
          { image: 420, viewport: 1024 },
          { image: 600, viewport: 1280 },
          { image: 800, viewport: 1536 },
        ];
      case 'background':
        return [
          { image: 380, viewport: 375 },
          { image: 420, viewport: 420 },
          { image: 600, viewport: 600 },
          { image: 1024, viewport: 1024 },
          { image: 1280, viewport: 1280 },
          { image: 1536, viewport: 1536 },
        ];
      default:
        return [
          { image: 380, viewport: 375 },
          { image: 420, viewport: 420 },
          { image: 600, viewport: 600 },
          { image: 800, viewport: 800 },
          { image: 1024, viewport: 1024 },
          { image: 1280, viewport: 1280 },
        ];
    }
  };
  return (
    <section
      className={clsx(
        paragraphStyles.paragraph,
        getWrapperClass(mediaPosition, isGrid),
        isRollover && 'group',
        data.bgColor && data.mediaPosition != 'background' && 'py-8',
        className,
        'paragraph',
        'paragraph-callout-paragraph'
      )}
      id={id}
    >
      {/* Image */}
      {mediaPosition !== 'icon' && data.mediaItem && (
        <MediaUnionComponent
          data={data.mediaItem}
          figureClassName={clsx(
            mediaPosition === 'background' && 'w-full h-full'
          )}
          imageAspect={getMediaAspect(
            mediaPosition,
            data.mediaAspect || undefined
          )}
          imageSizes={getImageSizes()}
          imageClassName={getImageClass(mediaPosition)}
          className={getMediaClass(mediaPosition)}
          noLoader={mediaPosition === 'background'}
        />
      )}
      {data.bgColor && !isRollover && (
        <div
          className={clsx(
            'absolute inset-0',
            '-z-10',
            mediaPosition === 'background' || isGrid
              ? ''
              : 'left-[50%] right-[50%] mx-[-50vw] w-screen'
          )}
          style={{ backgroundColor: `#${data.bgColor}` }}
        />
      )}
      {/* Content Wrapper */}
      <div
        className={clsx('paragraph-content', getContentClass(mediaPosition))}
      >
        {/* Rollover header overlay */}
        {data.header && isRollover && (
          <div className="absolute inset-0 z-0 flex items-center justify-center p-4 transition-opacity delay-150 duration-300 group-hover:pointer-events-none group-hover:opacity-0 group-hover:delay-0">
            <h2 className="h3 text-center text-white drop-shadow-md">
              {data.header}
            </h2>
          </div>
        )}

        {/* Main content */}
        {CalloutContent()}
      </div>
    </section>
  );
};
